import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Select,
  Spin,
  Typography,
  message,
  Image,
} from 'antd';
import {
  EyeOutlined,
  GlobalOutlined,
  MailOutlined,
  ShopOutlined,
  SyncOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { ROW_GUTTER } from 'constants/ThemeConstant';
import { useMyContext } from 'Context/MyContextProvider';
import SelectMediaModel from 'views/app-views/whatsapp/Campaign/CampainComps/SelectMediaModel';
import ProfilePreview from './ProfilePreview';
import CustomDrawer from 'components/shared-components/CustomDrawer';
import {
  fetchBusinessProfile,
  fetchBusinessProfileVerticals,
  refreshBusinessProfile,
  updateBusinessProfile,
  updateBusinessProfilePicture,
} from 'services/metaBusinessProfileService';

const { TextArea } = Input;
const { Title, Text, Paragraph } = Typography;

const optionalUrlRule = {
  validator: (_, value) => {
    const trimmed = String(value || '').trim();
    if (!trimmed) {
      return Promise.resolve();
    }
    try {
      // eslint-disable-next-line no-new
      new URL(trimmed);
      return Promise.resolve();
    } catch (error) {
      return Promise.reject(new Error('Enter a valid URL including https://'));
    }
  },
};
const LIMITS = {
  about: 139,
  address: 256,
  description: 512,
  email: 128,
  website: 256,
};

const formatVerticalLabel = (value) =>
  String(value || '')
    .split('_')
    .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
    .join(' ');

const BusinessProfile = ({ navigate, id }) => {
  const { Permisson } = useMyContext();
  const tenantUserId =
    id && Number.isFinite(Number(id)) && Number(id) > 0 ? Number(id) : undefined;
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [profile, setProfile] = useState(null);
  const [verticals, setVerticals] = useState([]);
  const [error, setError] = useState('');
  const [showMediaModel, setShowMediaModel] = useState(false);
  const [websiteCount, setWebsiteCount] = useState(1);
  const [previewDrawerOpen, setPreviewDrawerOpen] = useState(false);

  const canView = Permisson?.includes('View Edit Profile');
  const canUpdate = Permisson?.includes('Update Edit Profile');

  useEffect(() => {
    if (!canView) {
      navigate('404');
    }
  }, [Permisson, canView, navigate]);

  useEffect(() => {
    form.resetFields();
    setProfile(null);
    setError('');
    setWebsiteCount(1);
  }, [tenantUserId, form]);

  const applyProfileToForm = useCallback(
    (data) => {
      const websites = data?.websites?.length ? data.websites : [''];
      setWebsiteCount(Math.max(websites.length, 1));
      form.setFieldsValue({
        about: data?.about || '',
        description: data?.description || '',
        address: data?.address || '',
        email: data?.email || '',
        vertical: data?.vertical || undefined,
        website_0: websites[0] || '',
        website_1: websites[1] || '',
      });
    },
    [form],
  );

  const loadProfile = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [profileRes, verticalsRes] = await Promise.all([
        fetchBusinessProfile(tenantUserId),
        fetchBusinessProfileVerticals(),
      ]);

      if (profileRes?.ok && profileRes?.data) {
        setProfile(profileRes.data);
        applyProfileToForm(profileRes.data);
      } else {
        setError(profileRes?.error || 'Unable to load business profile.');
      }

      if (verticalsRes?.ok && Array.isArray(verticalsRes.data)) {
        setVerticals(verticalsRes.data);
      }
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Unable to load business profile.');
    } finally {
      setLoading(false);
    }
  }, [applyProfileToForm, tenantUserId]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const handleRefresh = async () => {
    setRefreshing(true);
    setError('');
    try {
      const response = await refreshBusinessProfile(tenantUserId);
      if (response?.ok && response?.data) {
        setProfile(response.data);
        applyProfileToForm(response.data);
        message.success('Profile refreshed from Meta.');
      } else {
        setError(response?.error || 'Refresh failed.');
      }
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Refresh failed.');
    } finally {
      setRefreshing(false);
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const websites = [values.website_0, values.website_1]
        .map((url) => String(url || '').trim())
        .filter(Boolean);

      setSaving(true);
      setError('');

      const response = await updateBusinessProfile({
        about: values.about,
        description: values.description,
        address: values.address,
        email: values.email,
        vertical: values.vertical,
        websites,
      }, tenantUserId);

      if (response?.ok && response?.data) {
        setProfile(response.data);
        applyProfileToForm(response.data);
        message.success('Business profile updated successfully.');
      } else {
        setError(response?.error || 'Update failed.');
      }
    } catch (err) {
      if (err?.errorFields) {
        return;
      }
      setError(err?.response?.data?.error || err?.message || 'Update failed.');
    } finally {
      setSaving(false);
    }
  };

  const isJpegMedia = (name, mimeType) => {
    const lowerName = String(name || '').toLowerCase();
    const lowerMime = String(mimeType || '').toLowerCase();
    if (['image/jpeg', 'image/jpg', 'image/pjpeg'].includes(lowerMime)) {
      return true;
    }
    return lowerName.endsWith('.jpg') || lowerName.endsWith('.jpeg');
  };

  const handleMediaSelect = async (mediaId, name, mimeType) => {
    if (!isJpegMedia(name, mimeType)) {
      message.warning('WhatsApp profile pictures must be JPEG images (.jpg or .jpeg).');
      return;
    }

    setShowMediaModel(false);
    setUploadingPicture(true);
    setError('');

    try {
      const response = await updateBusinessProfilePicture(mediaId, tenantUserId);
      if (response?.ok && response?.data) {
        setProfile(response.data);
        applyProfileToForm(response.data);
        message.success('Profile picture updated.');
      } else {
        setError(response?.error || 'Failed to update profile picture.');
      }
    } catch (err) {
      const apiError = err?.response?.data;
      if (apiError?.errors?.media_id) {
        message.error(apiError.errors.media_id[0]);
      } else {
        setError(apiError?.error || err?.message || 'Failed to update profile picture.');
      }
    } finally {
      setUploadingPicture(false);
    }
  };

  const watchedAbout = Form.useWatch('about', form);
  const watchedDescription = Form.useWatch('description', form);
  const watchedEmail = Form.useWatch('email', form);
  const watchedAddress = Form.useWatch('address', form);
  const watchedVertical = Form.useWatch('vertical', form);
  const watchedWebsite0 = Form.useWatch('website_0', form);
  const watchedWebsite1 = Form.useWatch('website_1', form);

  const previewProfile = {
    ...profile,
    about: watchedAbout ?? profile?.about,
    description: watchedDescription ?? profile?.description,
    email: watchedEmail ?? profile?.email,
    address: watchedAddress ?? profile?.address,
    vertical: watchedVertical ?? profile?.vertical,
    websites: [watchedWebsite0, watchedWebsite1].filter(Boolean),
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <>
      <SelectMediaModel
        openMediaModel={showMediaModel}
        handleCancelMediaModel={() => setShowMediaModel(false)}
        HandleUploadImage={(mediaId, name, url, mimeType) => handleMediaSelect(mediaId, name, mimeType)}
        requestType="template"
        handleCustomMedia={null}
        mediaKindFilter="image"
        mediaUserId={tenantUserId}
      />

      {error && (
        <Alert className="mb-3" type="error" showIcon message={error} closable onClose={() => setError('')} />
      )}

      <Row gutter={ROW_GUTTER}>
        <Col xs={24} xl={14}>
          <Card>
            <div className="d-flex justify-content-between align-items-start flex-wrap" style={{ gap: 12 }}>
              <div>
                <Title level={3} style={{ marginBottom: 4 }}>
                  WhatsApp Business Profile
                </Title>
                <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                  Manage your WhatsApp Business profile information visible to customers.
                </Paragraph>
              </div>
              <div className="d-flex" style={{ gap: 8 }}>
                <Button
                  className="d-xl-none"
                  icon={<EyeOutlined />}
                  onClick={() => setPreviewDrawerOpen(true)}
                >
                  Preview Profile
                </Button>
                <Button icon={<SyncOutlined />} onClick={handleRefresh} loading={refreshing}>
                  Refresh
                </Button>
              </div>
            </div>
            <div className="mb-4 d-flex align-items-center flex-wrap" style={{ gap: 16 }}>
              {profile?.profile_picture_url ? (
                <Image
                  src={profile.profile_picture_url}
                  width={96}
                  height={96}
                  style={{ borderRadius: '50%', objectFit: 'cover' }}
                />
              ) : (
                <Avatar
                  size={96}
                  icon={<ShopOutlined />}
                />
              )}
              <div>
                <div className="d-flex flex-wrap" style={{ gap: 8 }}>
                  {canUpdate && (
                    <Button
                      type="primary"
                      icon={<UploadOutlined />}
                      onClick={() => setShowMediaModel(true)}
                      loading={uploadingPicture}
                    >
                      Upload New Picture
                    </Button>
                  )}
                </div>
                <Text type="secondary" className="d-block mt-2">
                  Recommended: 640 x 640px, JPG only. Max size: 5MB.
                </Text>
              </div>
            </div>

            <Form form={form} layout="vertical">
              <Form.Item label="Business About" name="about">
                <TextArea
                  rows={2}
                  maxLength={LIMITS.about}
                  showCount
                  placeholder="Short tagline shown below your profile picture"
                />
              </Form.Item>

              <Form.Item label="Business Description" name="description">
                <TextArea
                  rows={4}
                  maxLength={LIMITS.description}
                  showCount
                  placeholder="Describe what your business does"
                />
              </Form.Item>

              <Row gutter={ROW_GUTTER}>
                <Col xs={24} md={12}>
                  <Form.Item label="Business Category" name="vertical">
                    <Select
                      allowClear
                      showSearch
                      placeholder="Select category"
                      optionFilterProp="label"
                      options={verticals.map((item) => ({
                        value: item,
                        label: formatVerticalLabel(item),
                      }))}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Business Email"
                    name="email"
                    rules={[
                      {
                        validator: (_, value) => {
                          const trimmed = String(value || '').trim();
                          if (!trimmed) return Promise.resolve();
                          const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                          return emailPattern.test(trimmed)
                            ? Promise.resolve()
                            : Promise.reject(new Error('Enter a valid email address'));
                        },
                      },
                    ]}
                  >
                    <Input prefix={<MailOutlined />} maxLength={LIMITS.email} placeholder="support@example.com" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item label="Business Address" name="address">
                <TextArea
                  rows={2}
                  maxLength={LIMITS.address}
                  showCount
                  placeholder="Street, city, state, postal code"
                />
              </Form.Item>

              <Form.Item label="Website" name="website_0" rules={[optionalUrlRule]}>
                <Input prefix={<GlobalOutlined />} maxLength={LIMITS.website} placeholder="https://www.example.com" />
              </Form.Item>

              {websiteCount > 1 && (
                <Form.Item label="Website 2" name="website_1" rules={[optionalUrlRule]}>
                  <Input prefix={<GlobalOutlined />} maxLength={LIMITS.website} placeholder="https://www.example.com" />
                </Form.Item>
              )}

              {websiteCount < 2 && (
                <Button type="link" className="px-0 mb-3" onClick={() => setWebsiteCount(2)}>
                  + Add Another Website
                </Button>
              )}

              <div className="d-flex justify-content-end flex-wrap" style={{ gap: 8 }}>
                <Button danger onClick={() => navigate('/users')}>
                  Cancel
                </Button>
                {canUpdate && (
                  <Button type="primary" onClick={handleSave} loading={saving}>
                    Save Changes
                  </Button>
                )}
              </div>
            </Form>
          </Card>
        </Col>

        <Col xs={0} xl={10}>
          <Card
            title={
              <div className="py-1">
                <div style={{ fontSize: '16px', fontWeight: '600', color: '#1a3353' }}>Profile Preview</div>
                <div className="mt-1" style={{ fontSize: '12px', fontWeight: '400', color: '#8798ad', whiteSpace: 'normal' }}>
                  This is how your profile appears to WhatsApp users.
                </div>
              </div>
            }
          >
            <ProfilePreview profile={previewProfile} />
          </Card>
        </Col>
      </Row>

      <CustomDrawer
        title={
          <div className="py-1">
            <div style={{ fontSize: '16px', fontWeight: '600', color: '#1a3353' }}>Profile Preview</div>
            <div className="mt-1" style={{ fontSize: '12px', fontWeight: '400', color: '#8798ad', whiteSpace: 'normal' }}>
              This is how your profile appears to WhatsApp users.
            </div>
          </div>
        }
        open={previewDrawerOpen}
        onClose={() => setPreviewDrawerOpen(false)}
        width={400}
      >
        <div style={{ padding: '0 8px' }}>
          <ProfilePreview profile={previewProfile} />
        </div>
      </CustomDrawer>
    </>
  );
};

export default BusinessProfile;
